import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  BoltIcon, 
  SunIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  SignalIcon,
  MoonIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../../hooks/useTheme';

const Live247: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [liveData, setLiveData] = useState({
    totalPower: 1750,
    efficiency: 92.5,
    temperature: 28.5,
    irradiance: 950,
    alerts: 0,
    uptime: 99.8
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      
      // Simulate live data updates with smaller variations
      setLiveData(prev => ({
        totalPower: prev.totalPower + (Math.random() - 0.5) * 100,
        efficiency: Math.max(85, Math.min(100, prev.efficiency + (Math.random() - 0.5) * 2)),
        temperature: Math.max(20, Math.min(40, prev.temperature + (Math.random() - 0.5) * 1)),
        irradiance: Math.max(700, Math.min(1200, prev.irradiance + (Math.random() - 0.5) * 50)),
        alerts: Math.floor(Math.random() * 3),
        uptime: Math.max(98, Math.min(100, prev.uptime + (Math.random() - 0.5) * 0.1))
      }));
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = useCallback((date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }, []);

  const formatDate = useCallback((date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, []);

  const themeClasses = useMemo(() => ({
    background: theme === 'dark' 
      ? 'bg-gradient-to-br from-emerald-950 via-teal-900 to-cyan-950 text-emerald-50' 
      : 'bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 text-emerald-900',
    button: theme === 'dark'
      ? 'bg-emerald-900/50 border-emerald-400/30 text-emerald-300 hover:bg-emerald-800/50'
      : 'bg-white/70 border-emerald-300/50 text-emerald-700 hover:bg-white/90'
  }), [theme]);

  return (
    <div className={`min-h-screen p-6 relative overflow-hidden ${themeClasses.background}`}>
      {/* Theme Toggle */}
      <div className="absolute top-6 right-6 z-50">
        <button
          onClick={toggleTheme}
          className={`p-3 rounded-2xl border transition-colors duration-200 ${themeClasses.button} backdrop-blur-xl shadow-lg`}
        >
          {theme === 'dark' ? (
            <SunIcon className="w-6 h-6" />
          ) : (
            <MoonIcon className="w-6 h-6" />
          )}
        </button>
      </div>
      {/* Simplified Background */}
      <div className={`absolute inset-0 ${theme === 'dark' ? 'opacity-5' : 'opacity-10'}`}>
        <div className="absolute top-20 left-20 w-2 h-2 bg-emerald-400 rounded-full" />
        <div className="absolute top-60 right-40 w-2 h-2 bg-cyan-400 rounded-full" />
        <div className="absolute bottom-20 right-20 w-2 h-2 bg-lime-400 rounded-full" />
      </div>
      {/* AI Header */}
      <div className="relative z-10 text-center mb-12">
        <h1 className={`text-6xl font-bold mb-4 bg-clip-text text-transparent ${
          theme === 'dark'
            ? 'bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400'
            : 'bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600'
        }`}>
          SOLAR INTELLIGENCE HUB
        </h1>
        <p className={`text-2xl mb-8 font-light tracking-wide ${
          theme === 'dark' ? 'text-emerald-300' : 'text-emerald-700'
        }`}>Sustainable Energy Intelligence • 24/7 Operations</p>
        
        <div className={`backdrop-blur-xl rounded-3xl p-8 border inline-block ${
          theme === 'dark'
            ? 'bg-emerald-950/50 border-emerald-400/30'
            : 'bg-white/70 border-emerald-300/50'
        }`}>
          <div className={`text-7xl font-mono font-bold mb-2 tracking-wider ${
            theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'
          }`}>
            {formatTime(currentTime)}
          </div>
          <div className={`text-xl font-light ${
            theme === 'dark' ? 'text-teal-300' : 'text-teal-600'
          }`}>
            {formatDate(currentTime)}
          </div>
          <div className="mt-4 flex justify-center gap-2">
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" style={{ animationDelay: '0.3s' }} />
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" style={{ animationDelay: '0.6s' }} />
          </div>
        </div>
      </div>

      {/* AI Metrics Grid */}
      <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
        {/* Neural Power Core */}
        <div className={`rounded-3xl p-8 text-center border ${
          theme === 'dark'
            ? 'bg-emerald-950/50 border-emerald-400/30'
            : 'bg-white/70 border-emerald-300/50'
        }`}>
          <BoltIcon className={`w-20 h-20 mx-auto mb-6 ${
            theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'
          }`} />
          <div className={`text-5xl font-bold mb-3 font-mono ${
            theme === 'dark' ? 'text-emerald-300' : 'text-emerald-700'
          }`}>
            {liveData.totalPower.toFixed(0)}
          </div>
          <div className={`text-xs mb-2 ${
            theme === 'dark' ? 'text-emerald-500' : 'text-emerald-600'
          }`}>KILOWATTS</div>
          <div className={`text-lg font-light ${
            theme === 'dark' ? 'text-emerald-200' : 'text-emerald-700'
          }`}>Clean Energy Core</div>
          <div className="mt-6 flex items-center justify-center gap-3">
            <div className="w-4 h-4 bg-emerald-400 rounded-full" />
            <span className={`text-sm font-semibold ${
              theme === 'dark' ? 'text-emerald-300' : 'text-emerald-700'
            }`}>ACTIVE</span>
          </div>
        </div>

        {/* AI Efficiency Matrix */}
        <div className={`rounded-3xl p-8 text-center border ${
          theme === 'dark'
            ? 'bg-emerald-950/50 border-lime-400/30'
            : 'bg-white/70 border-lime-300/50'
        }`}>
          <ChartBarIcon className={`w-20 h-20 mx-auto mb-6 ${
            theme === 'dark' ? 'text-lime-400' : 'text-lime-600'
          }`} />
          <div className={`text-5xl font-bold mb-3 font-mono ${
            theme === 'dark' ? 'text-lime-300' : 'text-lime-700'
          }`}>
            {liveData.efficiency.toFixed(1)}
          </div>
          <div className={`text-xs mb-2 ${
            theme === 'dark' ? 'text-lime-500' : 'text-lime-600'
          }`}>EFFICIENCY RATE</div>
          <div className={`text-lg font-light ${
            theme === 'dark' ? 'text-lime-200' : 'text-lime-700'
          }`}>Smart Optimization</div>
          <div className="mt-6">
            <div className={`w-full rounded-full h-3 ${
              theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'
            }`}>
              <div className="bg-lime-400 h-3 rounded-full" style={{ width: `${Math.min(100, liveData.efficiency)}%` }} />
            </div>
            <div className={`text-xs mt-3 ${
              theme === 'dark' ? 'text-lime-400' : 'text-lime-600'
            }`}>Target: 85% • Current: {liveData.efficiency.toFixed(1)}%</div>
          </div>
        </div>

        {/* Solar Intelligence Sensor */}
        <div className={`rounded-3xl p-8 text-center border ${
          theme === 'dark'
            ? 'bg-emerald-950/50 border-amber-400/30'
            : 'bg-white/70 border-amber-300/50'
        }`}>
          <SunIcon className={`w-20 h-20 mx-auto mb-6 ${
            theme === 'dark' ? 'text-amber-400' : 'text-amber-600'
          }`} />
          <div className={`text-5xl font-bold mb-3 font-mono ${
            theme === 'dark' ? 'text-amber-300' : 'text-amber-700'
          }`}>
            {liveData.irradiance.toFixed(0)}
          </div>
          <div className={`text-xs mb-2 ${
            theme === 'dark' ? 'text-amber-500' : 'text-amber-600'
          }`}>WATTS/M²</div>
          <div className={`text-lg font-light ${
            theme === 'dark' ? 'text-amber-200' : 'text-amber-700'
          }`}>Solar Intelligence</div>
          <div className="mt-6 grid grid-cols-3 gap-2">
            <div className={`h-2 rounded ${
              theme === 'dark' ? 'bg-amber-400' : 'bg-amber-500'
            }`} />
            <div className={`h-2 rounded ${
              theme === 'dark' ? 'bg-amber-400' : 'bg-amber-500'
            }`} />
            <div className={`h-2 rounded ${
              theme === 'dark' ? 'bg-amber-400' : 'bg-amber-500'
            }`} />
          </div>
          <div className={`text-xs mt-3 ${
            theme === 'dark' ? 'text-amber-400' : 'text-amber-600'
          }`}>Peak Capacity: 1000 W/m²</div>
        </div>
      </div>

      {/* Status Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* System Status */}
        <div className={`rounded-2xl p-6 border ${
          theme === 'dark'
            ? 'bg-emerald-950/50 border-emerald-700/30'
            : 'bg-white/70 border-emerald-200/50'
        }`}>
          <div className="flex items-center gap-3 mb-4">
            <CheckCircleIcon className={`w-8 h-8 ${
              theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'
            }`} />
            <h3 className={`text-xl font-semibold ${
              theme === 'dark' ? 'text-emerald-100' : 'text-emerald-900'
            }`}>System Status</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className={theme === 'dark' ? 'text-emerald-300' : 'text-emerald-700'}>Inverters Online</span>
              <span className={`font-semibold ${
                theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'
              }`}>24/24</span>
            </div>
            <div className="flex justify-between">
              <span className={theme === 'dark' ? 'text-emerald-300' : 'text-emerald-700'}>Panels Active</span>
              <span className={`font-semibold ${
                theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'
              }`}>1,200/1,200</span>
            </div>
            <div className="flex justify-between">
              <span className={theme === 'dark' ? 'text-emerald-300' : 'text-emerald-700'}>Uptime</span>
              <span className={`font-semibold ${
                theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'
              }`}>{liveData.uptime.toFixed(1)}%</span>
            </div>
          </div>
        </div>

        {/* Alerts */}
        <div className={`rounded-2xl p-6 border ${
          theme === 'dark'
            ? 'bg-amber-950/50 border-amber-700/30'
            : 'bg-white/70 border-amber-200/50'
        }`}>
          <div className="flex items-center gap-3 mb-4">
            <ExclamationTriangleIcon className={`w-8 h-8 ${
              theme === 'dark' ? 'text-amber-400' : 'text-amber-600'
            }`} />
            <h3 className={`text-xl font-semibold ${
              theme === 'dark' ? 'text-amber-100' : 'text-amber-900'
            }`}>Active Alerts</h3>
          </div>
          <div className="text-center">
            <div className={`text-4xl font-bold mb-2 ${
              theme === 'dark' ? 'text-amber-400' : 'text-amber-600'
            }`}>
              {liveData.alerts}
            </div>
            <div className={theme === 'dark' ? 'text-amber-300' : 'text-amber-700'}>
              {liveData.alerts === 0 ? 'All Systems Optimal' : 'Alerts Pending'}
            </div>
          </div>
        </div>

        {/* Environmental */}
        <div className={`rounded-2xl p-6 border min-h-[180px] ${
          theme === 'dark'
            ? 'bg-teal-950/50 border-teal-700/30'
            : 'bg-white/70 border-teal-200/50'
        }`}>
          <div className="flex items-center gap-3 mb-6">
            <SignalIcon className={`w-8 h-8 ${
              theme === 'dark' ? 'text-teal-400' : 'text-teal-600'
            }`} />
            <h3 className={`text-lg font-semibold ${
              theme === 'dark' ? 'text-teal-100' : 'text-teal-900'
            }`}>Environment</h3>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className={`text-sm ${theme === 'dark' ? 'text-teal-300' : 'text-teal-700'}`}>Temperature</span>
              <span className={`font-semibold text-sm ${
                theme === 'dark' ? 'text-teal-400' : 'text-teal-600'
              }`}>{liveData.temperature.toFixed(1)}°C</span>
            </div>
            <div className="flex justify-between items-center">
              <span className={`text-sm ${theme === 'dark' ? 'text-teal-300' : 'text-teal-700'}`}>Humidity</span>
              <span className={`font-semibold text-sm ${
                theme === 'dark' ? 'text-teal-400' : 'text-teal-600'
              }`}>45%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className={`text-sm ${theme === 'dark' ? 'text-teal-300' : 'text-teal-700'}`}>Wind Speed</span>
              <span className={`font-semibold text-sm ${
                theme === 'dark' ? 'text-teal-400' : 'text-teal-600'
              }`}>12 km/h</span>
            </div>
          </div>
        </div>
      </div>

      {/* Live Chart Placeholder */}
      <div className={`rounded-2xl p-8 border ${
        theme === 'dark'
          ? 'bg-emerald-950/30 border-emerald-700/30'
          : 'bg-white/70 border-emerald-200/50'
      }`}>
        <div className="flex items-center justify-between mb-6">
          <h3 className={`text-2xl font-semibold ${
            theme === 'dark' ? 'text-emerald-100' : 'text-emerald-900'
          }`}>Real-Time Power Generation</h3>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-emerald-400 rounded-full animate-pulse" />
            <span className={`${
              theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'
            }`}>Live Data Stream</span>
          </div>
        </div>
        
        {/* Simulated Chart Area */}
        <div className={`h-64 rounded-xl flex items-center justify-center border ${
          theme === 'dark'
            ? 'bg-emerald-900/20 border-emerald-800/30'
            : 'bg-emerald-50/50 border-emerald-200/30'
        }`}>
          <div className="text-center">
            <ChartBarIcon className={`w-16 h-16 mx-auto mb-4 ${
              theme === 'dark' ? 'text-emerald-600' : 'text-emerald-400'
            }`} />
            <p className={`${
              theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'
            }`}>Live Chart Visualization</p>
            <p className={`text-sm mt-2 ${
              theme === 'dark' ? 'text-emerald-500' : 'text-emerald-700'
            }`}>Power: {liveData.totalPower.toFixed(0)} kW</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={`mt-8 text-center ${
        theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'
      }`}>
        <p>Athenas Solar Intelligence System • Sustainable Operations Center</p>
        <p className={`text-sm mt-1 ${
          theme === 'dark' ? 'text-emerald-500' : 'text-emerald-700'
        }`}>Last Update: {formatTime(currentTime)}</p>
      </div>
    </div>
  );
};

export default Live247;